import { Bitcoin, CheckCircle2, Save, ShieldCheck, TriangleAlert } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { isPaymentWalletValid, PAYMENT_WALLET_META } from "@/lib/paymentWallets";

const WalletPreview = ({ type, value, onChange }) => {
  const meta = PAYMENT_WALLET_META[type];
  const valid = isPaymentWalletValid(type, value);
  const inputId = `admin-wallet-${type}-input`;

  return (
    <section className="rounded-md border border-slate-700 bg-slate-950/40 p-4" data-testid={`admin-wallet-${type}-editor`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {type === "btc" ? <Bitcoin className="h-5 w-5 text-amber-400" /> : <span className="text-lg font-bold text-emerald-400">₮</span>}
          <div>
            <h4 className="font-semibold text-white">{meta.name}</h4>
            <p className="text-xs text-slate-500">{meta.network}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs ${valid ? "text-emerald-400" : "text-amber-400"}`} data-testid={`admin-wallet-${type}-validation`}>
          {valid ? <CheckCircle2 className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
          {valid ? "Valid" : "Check address"}
        </span>
      </div>

      <label htmlFor={inputId} className="mb-2 block text-sm text-slate-400">Deposit address</label>
      <input
        id={inputId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
        autoComplete="off"
        data-testid={`admin-wallet-${type}-input`}
        className="input-dark w-full font-mono text-sm"
      />

      <div className="mt-4 grid min-h-48 place-items-center rounded-md border border-slate-800 bg-white p-3">
        {valid ? (
          <QRCodeSVG value={value.trim()} size={168} level="M" marginSize={2} data-testid={`admin-wallet-${type}-qr`} />
        ) : (
          <p className="max-w-48 text-center text-sm text-slate-500" data-testid={`admin-wallet-${type}-qr-empty`}>Enter a valid address to preview its QR code.</p>
        )}
      </div>
    </section>
  );
};

export const WalletEditor = ({ wallets, onChange, onSave, saving, message }) => {
  const canSave = Object.keys(PAYMENT_WALLET_META).every((type) => isPaymentWalletValid(type, wallets[type]));

  return (
    <div className="card-dark p-5" data-testid="admin-wallet-settings-card">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-cyan-400" />
            <h3 className="font-semibold text-white">Payment wallets</h3>
          </div>
          <p className="mt-1 text-sm text-slate-400">Updates Billing, account activation payments, QR codes, and Telegram messages immediately.</p>
          <p className="mt-2 text-xs text-amber-300">Saving permanently replaces the current BTC and USDT-TRC20 addresses.</p>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !canSave}
          data-testid="admin-wallet-save-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-[background-color,transform,opacity] hover:bg-cyan-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save wallets"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {Object.keys(PAYMENT_WALLET_META).map((type) => (
          <WalletPreview key={type} type={type} value={wallets[type] || ""} onChange={(value) => onChange(type, value)} />
        ))}
      </div>
      {message && <p className="mt-4 rounded-md border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-200" data-testid="admin-wallet-message">{message}</p>}
    </div>
  );
};